import { ClosedCaption, ClosedCaptionOff } from "@mui/icons-material";
import DoneIcon from "@mui/icons-material/Done";
import { MenuItem, MenuList } from "@mui/material";
import React, { useState } from "react";

import { useAppState } from "../../../../hooks/useAppState";
import { getCaptionLanguages, sendClosedCaptionTelemetryEvent } from "../../../../util/captionLanguages";
import {
  ButtonActionsContainer,
  CaptionsButton,
  CaptionsButtonContainer,
  Container,
  StyledListItemIcon,
  StyledListItemText,
  StyledPaper,
} from "./ButtonClosedCaptions.elements";

interface ButtonClosedCaptionsProps {
  readonly captionsEnabled: boolean;
  readonly onClickCaptions: (value: boolean) => void;
  readonly captionLanguage: string;
  readonly setCaptionLanguage: (lang: string) => void;
  readonly className?: string;
}

export default function ButtonClosedCaptions({
  captionsEnabled,
  onClickCaptions,
  captionLanguage,
  setCaptionLanguage,
  className,
}: ButtonClosedCaptionsProps): React.ReactElement {
  const { module } = useAppState();
  const [showMenu, setShowMenu] = useState(false);

  const { captionPaths } = module;

  const captionLanguages = getCaptionLanguages(captionPaths ?? [{ language: "en", path: "/video" }]);

  const handleClick = () => {
    setShowMenu(!showMenu);
  };

  const handleLanguageSelect = (value: string) => {
    onClickCaptions(value !== "off");
    setCaptionLanguage(value);
    setShowMenu(false);

    sendClosedCaptionTelemetryEvent(value);
  };

  return (
    <Container className={className}>
      <CaptionsButtonContainer>
        <CaptionsButton
          ariaLabel={`Closed captions, ${captionsEnabled ? "enabled" : "disabled"}`}
          icon={captionsEnabled ? <ClosedCaption /> : <ClosedCaptionOff />}
          FabProps={{ tabIndex: 1 }}
          onClick={handleClick}
          data-testid={`captions-button-${className}-${captionsEnabled ? "enabled" : "disabled"}`}
        />
        {showMenu && (
          <ButtonActionsContainer>
            <StyledPaper>
              <MenuList>
                {captionLanguages.map(lang => (
                  <MenuItem
                    key={lang.value}
                    onClick={() => handleLanguageSelect(lang.value)}
                    divider={lang.value === "off" && !captionsEnabled}
                    aria-label={`Closed captions, ${lang.value}`}
                    data-testid={`captions-menu-item-${lang.value}`}
                  >
                    <StyledListItemIcon>{lang.value === captionLanguage && <DoneIcon />}</StyledListItemIcon>
                    <StyledListItemText>{lang.display}</StyledListItemText>
                  </MenuItem>
                ))}
              </MenuList>
            </StyledPaper>
          </ButtonActionsContainer>
        )}
      </CaptionsButtonContainer>
    </Container>
  );
}
